import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Per-supplier Excel templates for 844 email claims.
 *
 * Some suppliers publish their own claim form and expect it back populated
 * (Omron's 17-column sheet, TE Connectivity's two-row grouped header plus a
 * reference sheet). For those, the workbook uploaded here REPLACES the default
 * 34-column attachment — we populate their file and send it instead of ours.
 *
 * Suppliers without a template are untouched and keep the default builder.
 */

/**
 * What can feed a template column.
 *
 * The first 34 are `SD844SentLine` attributes — the values frozen at send time,
 * so a template renders what actually went out rather than what the row says
 * today. The last three are derived at render time and have no column of their
 * own: `vdmNumber` comes from `sd_vdm_transactions`, `division` from the
 * submission, `sentAt` from the send timestamp.
 */
export const SD_844_TEMPLATE_SENT_LINE_FIELDS = [
  "tradingPartnerName",
  "sellingLocationName",
  "shippingLocationName",
  "batchControlNo",
  "invoiceNo",
  "orderLineNo",
  "supplierContractNo",
  "shipToCustomerNo",
  "soldToCustomerNo",
  "customerNameCity",
  "ourProductCode",
  "rebateType",
  "shipType",
  "shipQty",
  "buyersCost",
  "extendedBuyersCost",
  "spaCost",
  "extendedSpaCost",
  "claimAmount",
  "claimField",
  "orderNo",
  "invoiceDate",
  "ourAgreementNo",
  "upcItemCode",
  "sellingLocationCode",
  "shippingLocationCode",
  "supplierProductCode",
  "itemDescEnglish",
  "itemDescFrench",
  "dunsLocation",
  "suppBranchBpid",
  "suppCustomerShipToBpid",
  "suppCustomerSoldToBpid",
  "stockUom",
  "pricingUom",
  "vdmNumber",
  "division",
  "sentAt",
] as const;

/**
 * Every column of the 844D transaction (`sd_e844d`) with its display label.
 * The keys, in E844D layout order, ARE the bindable `tx.<column>` list — one
 * object rather than a list plus a label map, so the two can't drift. A parity
 * test pins the keys to `E844D_COLUMN_NAMES` (backend).
 */
const TRANSACTION_COLUMN_LABELS = {
  record_type: "Record Type D",
  record_count: "Record Count",
  company: "Company",
  division: "Division",
  file_group: "File Group",
  trans_type: "Transaction Type",
  trans_sub_type: "Transaction Subtype",
  trading_partner: "Trading Partner",
  duns_gl_branch: "DUNS + GL Branch",
  supplier_sell_location: "Supplier Sell Location",
  selling_location: "Selling Location",
  shipping_location: "Shipping Location",
  short_ship_location_name: "Short Ship Location Name",
  short_sell_location_name: "Short Sell Location Name",
  branch_name: "Branch Name",
  customer_name_city: "Customer Name/City",
  customer_name: "Customer Name",
  customer_addr_1: "Customer Address 1",
  customer_addr_2: "Customer Address 2",
  customer_city: "Customer City",
  customer_state: "Customer State/Province",
  customer_postal_code: "Customer Postal Code",
  customer_country: "Customer Country",
  rebate_type: "Rebate Type",
  supplier_contract_no: "Supplier Contract No.",
  agreement_no: "Agreement Number",
  sold_to_no: "Customer Sold #",
  ship_to_no: "Customer Ship #",
  order_no: "Order Number",
  backorder_level: "Backorder Level",
  backorder_level_100: "Backorder Level (100-XX)",
  assigned_id: "PAD01 Assigned ID",
  transfer_type_code: "PAD02 Transfer Type Code",
  line_assigned_id: "Line ID",
  vendor_catalogue_qualifier: "Vendor Catalog Code",
  vendor_catalog_no: "Vendor Catalog (Extended)",
  upc_qualifier: "UPC Code",
  upc_code: "UPC Number",
  vendor_product_qualifier: "Vendor Part Code",
  vendor_product_no: "Vendor Part Number",
  buyer_product_qualifier: "Buyer Part Code",
  buyer_product_no: "Buyer Part Number",
  uom: "Unit of Measure",
  unit_price_wholesale: "Wholesale Price",
  basis_uom_wholesale: "Wholesale UOM",
  unit_price_quoted: "Quote Price",
  basis_uom_quoted: "Quote UOM",
  quantity: "Quantity",
  quantity_uom: "Quantity UOM",
  claim_amount: "Claim Amount",
  invoice_no: "Invoice Number",
  order_line_no: "Line Number",
  order_line_ref: "Line Reference",
  invoice_date: "Invoice Date",
  ship_date: "Ship Date",
  order_date: "Order Date",
  buying_party_currency: "Currency (Buyer)",
  selling_party_currency: "Currency (Supplier)",
  accept_reject: "Accept Flag",
  reject_reason_code: "Reason Code",
  extract_control_no: "Extract Control No.",
  batch_control_no: "Batch Control No.",
  buyers_debit_memo: "Buyers Debit Memo",
  added_manually: "Manual Flag",
  batch_create_date: "Batch Date",
  batch_create_time: "Batch Time",
  email_status: "Email Status",
  sent_date: "Send Date",
  sent_time: "Send Time",
  resent_date: "Resent Date",
  resent_time: "Resent Time",
  last_modified_date: "Modified Date",
  last_modified_time: "Modified Time",
  last_modified_by: "Modified By",
  last_modified_program: "Modified Program",
  outbound_flag: "Outbound Flag",
  item_with_prefix: "Prefix Item",
  claim_number: "Claim Number",
  ship_type: "Ship Type",
  supplier_credit_memo: "Supplier Credit Memo",
  supplier_batch_id: "Supplier Batch ID",
  vendor_no: "Vendor Number",
  product_on_fly_flag: "Product on Fly Flag",
  edi_supplier_flag: "EDI Supplier Flag",
  previous_status: "Previous Status",
  landed_cost: "Landed Cost",
  last_po_number: "Last PO Number",
  last_po_vendor: "Last PO Vendor",
  price_class: "Product Class",
  price_sub_class: "Product Sub-Class",
  cost_override_flag: "Cost Override Flag",
} as const;

/**
 * Every column of the 844D transaction (`sd_e844d`), bindable as `tx.<column>`.
 *
 * The sent-line fields above are the 34 values of OUR default attachment. A
 * supplier's form asks for things that file never carried (customer address,
 * ship date, landed cost), so the whole 91-column E844D record is offered too.
 * Read from the transaction at render time; a SENT transaction is frozen, so
 * re-rendering a claim reproduces what went out.
 *
 * Positional and named exactly as `E844D_COLUMN_NAMES` (backend) — a parity
 * test keeps them in step.
 */
export const SD_844_TEMPLATE_TRANSACTION_COLUMNS = Object.keys(
  TRANSACTION_COLUMN_LABELS,
  // Object.keys widens to string[]; these ARE the label map's own keys.
) as Array<keyof typeof TRANSACTION_COLUMN_LABELS>;

/**
 * VDM (debit memo) attributes, bindable as `vdm.<attribute>` — the memo the
 * line was billed under (`sd_vdm_transactions`). Blank until AS400 confirms it,
 * which is also when an email claim is actually sent.
 */
export const SD_844_TEMPLATE_VDM_ATTRIBUTES = [
  "vdmNumber",
  "gentlyVdmNumber",
  "grossAmount",
  "gstHstAmount",
  "qstPstAmount",
  "totalTaxAmount",
  "totalAmount",
  "companyCode",
  "divisionCode",
  "branchCode",
  "shippingProvince",
  "specialInstructions1",
  "specialInstructions2",
  "specialInstructions3",
] as const;

export type Sd844TemplateTransactionColumn =
  (typeof SD_844_TEMPLATE_TRANSACTION_COLUMNS)[number];
export type Sd844TemplateVdmAttribute =
  (typeof SD_844_TEMPLATE_VDM_ATTRIBUTES)[number];

const TRANSACTION_FIELDS = SD_844_TEMPLATE_TRANSACTION_COLUMNS.map(
  (column) => `tx.${column}` as const,
);
const VDM_FIELDS = SD_844_TEMPLATE_VDM_ATTRIBUTES.map(
  (attribute) => `vdm.${attribute}` as const,
);

export const sd844TemplateFieldSchema = z.enum([
  ...SD_844_TEMPLATE_SENT_LINE_FIELDS,
  ...TRANSACTION_FIELDS,
  ...VDM_FIELDS,
]);

/** Which family a field belongs to — drives the grouped dropdown. */
export type Sd844TemplateFieldGroup = "claim" | "transaction" | "vdm";

export function sd844TemplateFieldGroup(
  field: string,
): Sd844TemplateFieldGroup {
  if (field.startsWith("tx.")) return "transaction";
  if (field.startsWith("vdm.")) return "vdm";
  return "claim";
}

const SENT_LINE_FIELD_LABELS: Record<
  (typeof SD_844_TEMPLATE_SENT_LINE_FIELDS)[number],
  string
> = {
  tradingPartnerName: "Trading Partner Name",
  sellingLocationName: "Sell Loc",
  shippingLocationName: "Ship Loc",
  batchControlNo: "Batch Ctrl#",
  invoiceNo: "Invoice No",
  orderLineNo: "Order line No",
  supplierContractNo: "SPA Quote No",
  shipToCustomerNo: "Cust ShipTo",
  soldToCustomerNo: "Cust Soldto",
  customerNameCity: "Customer Name/City",
  ourProductCode: "Our Product Code",
  rebateType: "Rebate Type",
  shipType: "Ship Type",
  shipQty: "Ship Qty",
  buyersCost: "Buyer's Cost Per Unit",
  extendedBuyersCost: "Extended Buyer's Cost",
  spaCost: "SPA Cost per Unit",
  extendedSpaCost: "Extended SPA Cost",
  claimAmount: "Claim Amt",
  claimField: "Claim Field",
  orderNo: "Order No",
  invoiceDate: "Invoice Date",
  ourAgreementNo: "Our agreement No",
  upcItemCode: "UPC Item Code",
  sellingLocationCode: "Sell Loc code",
  shippingLocationCode: "Ship Loc Code",
  supplierProductCode: "Supplier Product Code",
  itemDescEnglish: "Item Desc EN",
  itemDescFrench: "Item Desc FR",
  dunsLocation: "Duns Loc",
  suppBranchBpid: "Supp Br BPID",
  suppCustomerShipToBpid: "Supp-Customer Shipto BPID",
  suppCustomerSoldToBpid: "Supp-Customer Sold to BPID",
  stockUom: "Stock UOM",
  pricingUom: "Pricing UOM",
  vdmNumber: "VDM #",
  division: "Division",
  sentAt: "Sent At",
};

const VDM_ATTRIBUTE_LABELS: Record<Sd844TemplateVdmAttribute, string> = {
  vdmNumber: "VDM Number (AS400)",
  gentlyVdmNumber: "Gently VDM Number",
  grossAmount: "Gross Amount",
  gstHstAmount: "GST/HST",
  qstPstAmount: "QST/PST",
  totalTaxAmount: "Total Tax",
  totalAmount: "Total Amount",
  companyCode: "Company",
  divisionCode: "Division",
  branchCode: "Branch",
  shippingProvince: "Shipping Province",
  specialInstructions1: "Special Instructions 1",
  specialInstructions2: "Special Instructions 2",
  specialInstructions3: "Special Instructions 3",
};

/**
 * Human labels for the mapping dropdown.
 *
 * The claim fields deliberately use the SAME wording as the default
 * attachment's column headers (`EMAIL_REPORT_COLUMNS`), so someone binding a
 * supplier's column is picking from vocabulary they already recognise from the
 * file we send today — and from the "(Column X)" notes the S&D team writes in
 * row 2 of an uploaded form.
 *
 * Exported as a constant rather than served by a procedure: it is static, and
 * a dropdown should not cost a round trip on a ~0.25 Mbps link.
 */
export const SD_844_TEMPLATE_FIELD_LABELS = {
  ...SENT_LINE_FIELD_LABELS,
  ...Object.fromEntries(
    SD_844_TEMPLATE_TRANSACTION_COLUMNS.map((column) => [
      `tx.${column}`,
      TRANSACTION_COLUMN_LABELS[column],
    ]),
  ),
  ...Object.fromEntries(
    SD_844_TEMPLATE_VDM_ATTRIBUTES.map((attribute) => [
      `vdm.${attribute}`,
      VDM_ATTRIBUTE_LABELS[attribute],
    ]),
  ),
} as Record<z.infer<typeof sd844TemplateFieldSchema>, string>;

/**
 * Fields whose value is a date, and for which a display format is meaningful.
 *
 * Single source: the renderer, the suggestion matcher and the editor's format
 * dropdown all read this. Three copies of the list is how a field gains a
 * format in one place that another silently ignores.
 */
export const SD_844_TEMPLATE_DATE_FIELDS = [
  "invoiceDate",
  // The transaction's own dates are stored as the raw AS400 YYYYMMDD integer.
  "tx.invoice_date",
  "tx.ship_date",
  "tx.order_date",
] as const;

export const sd844TemplateDateFormatSchema = z.enum([
  "YYYY-MM-DD",
  "MM/DD/YYYY",
  "DD/MM/YYYY",
]);

/** True when a display format can meaningfully apply to this field. */
export function isSd844TemplateDateField(field: string): boolean {
  return (SD_844_TEMPLATE_DATE_FIELDS as readonly string[]).includes(field);
}

/**
 * Where a column's value comes from.
 *
 * `blank` is load-bearing, not a placeholder: Omron's form carries `Resale
 * Price`, `Intelligent Code` and `Special Pricing`, for which we hold no data.
 * The alternative to an empty cell is inventing a number on a money claim.
 */
export const sd844TemplateColumnSourceSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("field"),
    field: sd844TemplateFieldSchema,
    /**
     * Only meaningful for date-valued fields. TE's form demands
     * `Ship Date (YYYY-MM-DD)`; our `invoiceDate` is stored as `YYYYMMDD`.
     */
    dateFormat: sd844TemplateDateFormatSchema.optional(),
  }),
  z.object({
    kind: z.literal("constant"),
    value: z.string().max(255),
  }),
  z.object({ kind: z.literal("blank") }),
]);

export const sd844TemplateColumnMappingSchema = z.object({
  /** 1-based column of the target sheet — the sheet's own numbering. */
  columnIndex: z.number().int().min(1).max(256),
  /**
   * The header text we parsed out of the uploaded file, kept for orientation in
   * the editor. Never written back — the supplier's header is theirs.
   */
  headerLabel: z.string().max(255),
  /**
   * The mapping note the S&D team wrote under the header (row 2 of the forms
   * they hand us: "SPA Quote No (Column G)", "CAD"). Kept so the editor can show
   * it next to the binding it produced; never written into the claim.
   */
  hint: z.string().max(255).optional(),
  /**
   * Set when the binding came from a match that is not certain (a column
   * letter, a highlight, a literal, the model). The editor shows "Check this
   * match" until someone picks a value for the column. Display state only —
   * never affects rendering, and is left out of the template fingerprint.
   */
  needsReview: z.boolean().optional(),
  source: sd844TemplateColumnSourceSchema,
});

/**
 * The built-in default 844 attachment's columns, A onward — the 34 fields of
 * `EMAIL_REPORT_COLUMNS` plus the trailing "VDM #" the deferred send adds. It
 * is the sent-line list minus the two render-time extras with no column of
 * their own. This is also the file the team's "(Column X)" notes point into.
 * A backend parity test pins it to `EMAIL_REPORT_COLUMNS`.
 */
export const SD_844_DEFAULT_LAYOUT_FIELDS =
  SD_844_TEMPLATE_SENT_LINE_FIELDS.filter(
    (field) => field !== "division" && field !== "sentAt",
  );

/**
 * The built-in default expressed as template bindings: column N is field N,
 * headed with its own label. What "Customize Default" saves, and what the
 * default page shows before anyone has.
 */
export function builtInDefaultColumnMappings(): Sd844TemplateColumnMapping[] {
  return SD_844_DEFAULT_LAYOUT_FIELDS.map((field, index) => ({
    columnIndex: index + 1,
    headerLabel: SD_844_TEMPLATE_FIELD_LABELS[field],
    source: { kind: "field" as const, field },
  }));
}

export const sd844EmailTemplateSchema = z.object({
  id: z.uuid(),
  /** `null` = the organization's default template. */
  companyId: z.uuid().nullable(),
  originalFilename: z.string(),
  byteSize: z.number().int(),
  checksum: z.string(),
  sheetName: z.string(),
  dataStartRow: z.number().int(),
  columnMappings: z.array(sd844TemplateColumnMappingSchema),
  /** Sheets removed from the file before it is sent (e.g. an internal "Example" tab). */
  excludedSheetNames: z.array(z.string()),
  /** Every sheet of the stored file; empty for templates saved before it was recorded. */
  sheetNames: z.array(z.string()),
  /** Disabled templates remain editable but are ignored by future sends. */
  isEnabled: z.boolean(),
  uploadedByUserId: z.uuid().nullable(),
  uploadedByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

/** One sheet of an uploaded workbook, as offered to the mapping editor. */
export const sd844TemplateSheetPreviewSchema = z.object({
  name: z.string(),
  /**
   * Where we think the data should start. A best guess from the last non-empty
   * row near the top — always shown and always overridable, because the person
   * uploading knows their supplier's form and we are reading tea leaves.
   */
  suggestedDataStartRow: z.number().int(),
  /**
   * The row holding the S&D team's mapping notes ("SPA Quote No (Column G)"),
   * when one sits under the header. `null` when the form has none. That row is
   * an instruction to us, not part of the supplier's form, so the suggested data
   * start row OVERWRITES it.
   */
  hintRow: z.number().int().nullable(),
  /**
   * True when this sheet's header is our own default 844 layout — the "Example"
   * tab the team pastes in for reference. It should not go to the supplier, so
   * the editor proposes excluding it.
   */
  looksLikeDefaultLayout: z.boolean(),
  /** Detected columns, in sheet order, with the header text we found. */
  columns: z.array(
    z.object({
      columnIndex: z.number().int(),
      headerLabel: z.string(),
      /** The mapping note under this column, if the sheet has a hint row. */
      hint: z.string().optional(),
      /** Highlighted yellow — the team's convention for "leave this blank". */
      highlighted: z.boolean().optional(),
    }),
  ),
});

/**
 * A proposed binding for one column. `field: null` is "couldn't determine" —
 * surfaced to the user as exactly that, rather than an empty dropdown that
 * looks like a deliberate choice.
 */
export const sd844TemplateMappingSuggestionSchema = z.object({
  columnIndex: z.number().int(),
  field: sd844TemplateFieldSchema.nullable(),
  dateFormat: sd844TemplateDateFormatSchema.optional(),
  /** `exact` = our own label matched; `likely` = proposed; `none` = unresolved. */
  confidence: z.enum(["exact", "likely", "none"]),
  /**
   * The full proposed binding when it is not a field — a fixed value the
   * mapping note gives ("CAD"), or a deliberate blank (a highlighted column, or
   * one the notes leave empty). Absent means "use `field`".
   */
  source: sd844TemplateColumnSourceSchema.optional(),
  /** Why this was proposed, in words the person reviewing it can check. */
  reason: z.string().optional(),
});

/**
 * Which template: a supplier's own (`companyId`), or `null` for the
 * ORGANIZATION DEFAULT — the file every email supplier without its own form
 * receives. With no default uploaded, the built-in workbook is used.
 */
const templateCompanyIdSchema = z.uuid().nullable();

const templateRefSchema = z.object({ companyId: templateCompanyIdSchema });

const excludedSheetNamesSchema = z.array(z.string().trim().min(1)).max(50);

const uploadedWorkbookSchema = z.object({
  filename: z.string().trim().min(1).max(255),
  /** base64-encoded .xlsx bytes. Templates are a few KB; no streaming needed. */
  contentBase64: z.string().min(1),
});

const getTemplateContract = oc
  .input(templateRefSchema)
  .output(z.object({ template: sd844EmailTemplateSchema.nullable() }))
  .route({
    method: "GET",
    summary: "Get a supplier's 844 email template, if it has one",
    description:
      "Returns the claim email workbook template and column mappings for one supplier, or the organization default when companyId is null.",
  });

/**
 * Parse an uploaded workbook so the editor can offer its sheets and columns.
 *
 * Deliberately separate from `save`: the mapping cannot be authored until the
 * columns are known, so the upload is inspected first and stored only once the
 * bindings are set.
 */
const parseUploadContract = oc
  .input(uploadedWorkbookSchema)
  .output(z.object({ sheets: z.array(sd844TemplateSheetPreviewSchema) }))
  .route({
    summary: "Inspect an uploaded 844 template workbook",
    description:
      "Reads an uploaded Excel claim template and returns its sheets, header columns and hints so mappings can be authored. Saves nothing.",
  });

/**
 * Propose a source for each column of an uploaded workbook.
 *
 * Separate from `parseUpload` so the editor can render its columns immediately
 * and fill the suggestions in when they arrive — a model round-trip must not
 * sit between dropping a file and seeing it.
 */
const suggestMappingsContract = oc
  .input(
    z.object({
      columns: z
        .array(
          z.object({
            columnIndex: z.number().int().min(1).max(256),
            headerLabel: z.string().max(255),
            hint: z.string().max(255).optional(),
            highlighted: z.boolean().optional(),
          }),
        )
        .max(256),
      /**
       * True when the sheet carries a mapping-notes row. A column it leaves
       * empty is then a deliberate blank, not something to guess at.
       */
      hasHintRow: z.boolean().optional(),
    }),
  )
  .output(
    z.object({
      suggestions: z.array(sd844TemplateMappingSuggestionSchema),
    }),
  )
  .route({
    summary: "Suggest a source field for each template column",
    description:
      "Uses AI to propose which claim transaction field should fill each template column, based on header labels and hint notes. Saves nothing.",
  });

const saveTemplateContract = oc
  .input(
    z.object({
      companyId: templateCompanyIdSchema,
      /**
       * Omit to re-save mappings against the workbook already stored. Supplying
       * it replaces the file — which is how a supplier re-issuing their form is
       * handled.
       */
      upload: uploadedWorkbookSchema.optional(),
      sheetName: z.string().trim().min(1),
      dataStartRow: z.number().int().min(1).max(1000),
      columnMappings: z.array(sd844TemplateColumnMappingSchema),
      excludedSheetNames: excludedSheetNamesSchema.optional(),
    }),
  )
  .output(z.object({ template: sd844EmailTemplateSchema }))
  .route({
    summary: "Create or replace a supplier's 844 email template",
    description:
      "Saves a supplier's (or the organization default) claim email workbook, sheet, start row and column mappings; an uploaded file replaces the stored one.",
  });

const deleteTemplateContract = oc
  .input(templateRefSchema)
  .output(z.object({ deleted: z.boolean() }))
  .route({
    summary:
      "Remove a supplier's 844 email template (reverts it to the default attachment)",
    description:
      "Deletes the supplier's custom claim email workbook so future emailed claims use the organization default or built-in workbook.",
  });

const setTemplateEnabledContract = oc
  .input(
    templateRefSchema.extend({
      enabled: z.boolean(),
      /**
       * Version the editor actually reviewed. Refusing a stale version prevents
       * one editor from publishing a different draft saved by another editor.
       */
      expectedUpdatedAt: z.iso.datetime(),
    }),
  )
  .output(z.object({ template: sd844EmailTemplateSchema }))
  .route({
    summary: "Enable or disable an 844 email template",
    description:
      "Turns a supplier or default claim email template on or off for outgoing claims; refuses the change if the template was edited since it was reviewed.",
  });

/**
 * Which real transactions a test render is built from.
 *
 * `staged` — the supplier's newest staged lines: what the NEXT claim will look
 * like, which is the question someone setting up a template is asking. No VDM
 * exists yet at that point, so VDM columns render empty.
 * `sent` — the newest email claim actually sent, VDM numbers and all.
 */
export const sd844TemplateSampleSourceSchema = z.enum(["staged", "sent"]);

/**
 * Template settings that have not been saved yet.
 *
 * A test render takes them so the person can check a binding BEFORE it is
 * live — waiting until after save to see the file is how a wrong column reaches
 * a supplier. `upload` omitted renders the workbook already stored.
 */
export const sd844TemplateDraftSchema = z.object({
  upload: uploadedWorkbookSchema.optional(),
  sheetName: z.string().trim().min(1),
  dataStartRow: z.number().int().min(1).max(1000),
  columnMappings: z.array(sd844TemplateColumnMappingSchema),
  excludedSheetNames: excludedSheetNamesSchema.optional(),
});

const testRenderInputSchema = templateRefSchema.extend({
  /** Defaults to `sent`, which is what this procedure always rendered. */
  sample: sd844TemplateSampleSourceSchema.optional(),
  /** Omit to render the saved template. */
  draft: sd844TemplateDraftSchema.optional(),
});

/** What the sample lines were — so the UI can say exactly what it rendered. */
const sampleDescriptionSchema = z.object({
  source: sd844TemplateSampleSourceSchema,
  lineCount: z.number().int(),
  /** Invoice numbers of the rendered lines, newest first (capped). */
  invoiceNumbers: z.array(z.string()),
});

/**
 * Render the template against this supplier's real lines and return the file.
 *
 * The point is to see the exact file before a claim rides on it: the template's
 * own cell formats apply to whatever we write, so a mis-bound column renders
 * convincingly wrong rather than obviously wrong.
 */
const previewTemplateContract = oc
  .input(testRenderInputSchema)
  .output(
    z.object({
      filename: z.string(),
      /** base64-encoded .xlsx bytes */
      contentBase64: z.string(),
      /** How many real lines the preview was built from. */
      lineCount: z.number().int(),
      sample: sampleDescriptionSchema.optional(),
    }),
  )
  .route({
    summary: "Preview a supplier's 844 email template, populated",
    description:
      "Renders the saved or draft template with the supplier's newest staged or sent claim lines and returns the Excel file. Sends nothing.",
  });

/**
 * The value each column would get for ONE real sample line, as text.
 *
 * Shown beside every binding in the editor, so a wrong mapping is visible
 * without downloading anything — a few hundred bytes instead of a workbook on a
 * ~0.25 Mbps link.
 */
const sampleValuesContract = oc
  .input(
    templateRefSchema.extend({
      sample: sd844TemplateSampleSourceSchema,
      columnMappings: z.array(sd844TemplateColumnMappingSchema).max(256),
    }),
  )
  .output(
    z.object({
      /** `null` when the supplier has no line of that kind to sample. */
      sample: sampleDescriptionSchema.nullable(),
      values: z.array(
        z.object({
          columnIndex: z.number().int(),
          /** `null` = the cell is left empty. */
          value: z.string().nullable(),
        }),
      ),
    }),
  )
  .route({
    summary: "Resolve template bindings against one real 844 line",
    description:
      "Returns the text value each mapped column would get for one recent staged or sent claim line, so bindings can be checked inline.",
  });

/** One supplier row on the 844 Configuration page. */
export const sd844TemplateSupplierRowSchema = z.object({
  companyId: z.uuid(),
  name: z.string(),
  /** Unset in config means EDI, which is the module default. */
  deliveryMethod: z.enum(["edi", "email"]),
  template: z
    .object({
      originalFilename: z.string(),
      mappedColumnCount: z.number().int(),
      uploadedByName: z.string().nullable(),
      isEnabled: z.boolean(),
      updatedAt: z.string(),
    })
    .nullable(),
});

const listSuppliersContract = oc
  .output(
    z.object({
      suppliers: z.array(sd844TemplateSupplierRowSchema),
      /**
       * The org-wide default template, when one has been uploaded. Every email
       * supplier with `template: null` receives this file; with none, the
       * built-in default workbook.
       */
      defaultTemplate: sd844TemplateSupplierRowSchema.shape.template,
    }),
  )
  .route({
    method: "GET",
    summary: "Every S&D supplier and whether its 844 uses a custom template",
    description:
      "Lists ship and debit suppliers with claim delivery method (EDI or email) and custom template details, plus the organization default template.",
  });

/**
 * The built-in default 844 workbook, empty — the starting point for a custom
 * default: download, edit in Excel, upload it back as the org default.
 */
const downloadBuiltInDefaultContract = oc
  .output(z.object({ filename: z.string(), contentBase64: z.string() }))
  .route({
    summary: "Download the built-in default 844 workbook (no lines)",
    description:
      "Returns the empty built-in claim email workbook as base64 Excel, as a starting point for a custom organization default.",
  });

/**
 * Save the built-in default as the org default template, so it can be edited
 * like any other. Refused when a default already exists — it would overwrite
 * someone's customization.
 */
const createDefaultFromBuiltInContract = oc
  .output(z.object({ template: sd844EmailTemplateSchema }))
  .route({
    summary: "Create the org default 844 template from the built-in one",
    description:
      "Saves the built-in claim workbook as the editable organization default template; refused if a default already exists.",
  });

export const sd844EmailTemplatesContracts = {
  get: getTemplateContract,
  parseUpload: parseUploadContract,
  suggestMappings: suggestMappingsContract,
  save: saveTemplateContract,
  setEnabled: setTemplateEnabledContract,
  delete: deleteTemplateContract,
  preview: previewTemplateContract,
  sampleValues: sampleValuesContract,
  listSuppliers: listSuppliersContract,
  downloadBuiltInDefault: downloadBuiltInDefaultContract,
  createDefaultFromBuiltIn: createDefaultFromBuiltInContract,
};

export type Sd844TemplateField = z.infer<typeof sd844TemplateFieldSchema>;
export type Sd844TemplateColumnSource = z.infer<
  typeof sd844TemplateColumnSourceSchema
>;
export type Sd844TemplateColumnMapping = z.infer<
  typeof sd844TemplateColumnMappingSchema
>;
export type Sd844EmailTemplate = z.infer<typeof sd844EmailTemplateSchema>;
export type Sd844TemplateSheetPreview = z.infer<
  typeof sd844TemplateSheetPreviewSchema
>;
export type Sd844TemplateMappingSuggestion = z.infer<
  typeof sd844TemplateMappingSuggestionSchema
>;
export type Sd844TemplateSampleSource = z.infer<
  typeof sd844TemplateSampleSourceSchema
>;
export type Sd844TemplateDraft = z.infer<typeof sd844TemplateDraftSchema>;
export type Sd844TemplateSupplierRow = z.infer<
  typeof sd844TemplateSupplierRowSchema
>;
